import { io, Socket as SocketIOSocket } from 'socket.io-client';

export interface ISocket {
    id?: string;
    connected: boolean;
    on(event: string, callback: (...args: any[]) => void): void;
    off(event: string, callback?: (...args: any[]) => void): void;
    emit(event: string, data?: any): void;
    disconnect(): void;
    connect(): void;
}

const AUTH_TOKEN_KEY = 'auth_token';

function readStoredToken(): string | null {
    try {
        return localStorage.getItem(AUTH_TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

function withToken(url: string, token: string | null): string {
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(token)}`;
}

function normalizeSocketUrl(url: string): string {
    if (!url) return url;
    const trimmedUrl = url.trim();
    if (/^wss:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(trimmedUrl)) {
        return trimmedUrl.replace(/^wss:\/\//i, 'ws://');
    }
    return trimmedUrl;
}

const JOIN_EVENTS = new Set([
    'join_room',
    'subscribe',
    'subscribe-business-account',
    'subscribe-campaign',
    'join_conversation',
]);
// Every leave event, counted against the room it names. A feature may leave
// with a different event than it joined with (the WhatsApp inbox joins with
// `join_room conversation_<id>` and leaves with `leave_conversation`) - both
// name the same room, so they balance.
const UNSUBSCRIBE_EVENTS = new Set([
    'leave_room',
    'unsubscribe',
    'unsubscribe-business-account',
    'unsubscribe-campaign',
    'leave_conversation',
]);

/** The room a join/leave event names, or null for any other event. */
function roomIdFor(event: string, data?: any): string | null {
    switch (event) {
        case 'join_room':
        case 'subscribe':
        case 'leave_room':
        case 'unsubscribe':
            return (typeof data === 'string' ? data : data?.roomId) ?? null;
        case 'subscribe-business-account':
        case 'unsubscribe-business-account': {
            const id = typeof data === 'string' ? data : data?.businessAccountId ?? data;
            return id ? `business-account-${id}` : null;
        }
        case 'subscribe-campaign':
        case 'unsubscribe-campaign': {
            const id = typeof data === 'string' ? data : data?.campaignId ?? data;
            return id ? `campaign-${id}` : null;
        }
        case 'join_conversation':
        case 'leave_conversation': {
            const id = typeof data === 'string' ? data : data?.conversationId;
            return id ? `conversation_${id}` : null;
        }
        default:
            return null;
    }
}

/*
 * 🔴 ONE SOCKET, MANY FEATURES - ROOMS ARE SHARED, SO THEY ARE COUNTED.
 *
 * Team chat, the inbox, notifications, templates and campaigns all join rooms
 * on the same socket, often the same room (`business-account-…`). Each join
 * and leave used to go straight to the server, so the first feature to leave
 * a room unsubscribed it for every other feature still relying on it - e.g.
 * leaving the inbox stopped template status updates until a reload.
 *
 * Now every join adds one to the room's count and every leave takes one off.
 * The server is told to subscribe on the first join and to unsubscribe only
 * when the last user of the room leaves. A leave for a room this socket never
 * joined is ignored. The rooms still held are rejoined after a reconnect,
 * with the event that first joined them.
 */
class RoomRegistry {
    private rooms = new Map<string, { count: number; event: string; data: any }>();

    /** Count a join; true when this is the room's first user. */
    acquire(roomId: string, event: string, data: any): boolean {
        const held = this.rooms.get(roomId);
        if (held) {
            held.count += 1;
            return false;
        }
        this.rooms.set(roomId, { count: 1, event, data });
        return true;
    }

    /** Count a leave; true when the room's last user has left. */
    release(roomId: string): boolean {
        const held = this.rooms.get(roomId);
        if (!held) return false;
        held.count -= 1;
        if (held.count > 0) return false;
        this.rooms.delete(roomId);
        return true;
    }

    /** Rooms still in use, with the event that joined each. */
    held(): Array<{ roomId: string; event: string; data: any }> {
        return [...this.rooms.entries()].map(([roomId, r]) => ({ roomId, event: r.event, data: r.data }));
    }

    clear(): void {
        this.rooms.clear();
    }
}

export class SocketIOAdapter implements ISocket {
    private socket: SocketIOSocket;
    private rooms = new RoomRegistry();
    /*
     * The Socket.IO server forgets a socket's rooms when it reconnects. Set on
     * a reconnect, and acted on at the next `authenticated` - the server only
     * admits chat and tenant rooms once the socket is authenticated again.
     */
    private rejoinPending = false;
    private hasConnectedOnce = false;

    constructor(url: string, options?: any) {
        this.socket = io(url, options);
        this.socket.on('connect', () => {
            if (this.hasConnectedOnce) this.rejoinPending = true;
            this.hasConnectedOnce = true;
        });
        this.socket.on('authenticated', () => {
            if (!this.rejoinPending) return;
            this.rejoinPending = false;
            for (const { event, data } of this.rooms.held()) this.socket.emit(event, data);
        });
    }

    get id() { return this.socket.id; }
    get connected() { return this.socket.connected; }

    on(event: string, callback: (...args: any[]) => void): void {
        this.socket.on(event, callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        this.socket.off(event, callback);
    }

    emit(event: string, data?: any): void {
        const roomId = roomIdFor(event, data);
        if (roomId && JOIN_EVENTS.has(event)) {
            if (this.rooms.acquire(roomId, event, data)) this.socket.emit(event, data);
            return;
        }
        if (roomId && UNSUBSCRIBE_EVENTS.has(event)) {
            if (this.rooms.release(roomId)) this.socket.emit(event, data);
            return;
        }
        this.socket.emit(event, data);
    }

    disconnect(): void {
        this.rooms.clear();
        this.rejoinPending = false;
        this.socket.disconnect();
    }

    connect(): void {
        this.socket.connect();
    }
}

const PING_INTERVAL_MS = 4 * 60 * 1000;
const PONG_TIMEOUT_MS = 10 * 1000;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30 * 1000;
const RECONNECT_JITTER = 0.2;

export class ServerlessWebSocketAdapter implements ISocket {
    private ws: WebSocket | null = null;
    private url: string;
    private eventListeners: Map<string, Set<Function>> = new Map();

    private rooms = new RoomRegistry();
    private lastAuthPayload: any = null;

    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private pongTimer: ReturnType<typeof setTimeout> | null = null;
    private intentionalClose = false;
    private hasConnectedOnce = false;

    public id?: string;
    public connected: boolean = false;

    private getToken: () => string | null;

    constructor(url: string, getToken: () => string | null = readStoredToken) {
        this.url = url;
        this.getToken = getToken;
        this.connect();
    }

    connect(): void {
        this.intentionalClose = false;
        this.clearReconnectTimer();

        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

        const token = this.getToken();
        if (!token) {
            console.warn(
                '[Socket] connecting WITHOUT a token — the connection will be anonymous and realtime will be empty',
            );
        }

        try {
            this.ws = new WebSocket(withToken(this.url, token));
        } catch (err) {
            this._trigger('connect_error', err);
            this.scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.connected = true;
            this.reconnectAttempts = 0;

            this.replaySessionState();
            this.startPing();

            if (this.hasConnectedOnce) {
                this._trigger('reconnect', this.reconnectAttempts);
            } else {
                this.hasConnectedOnce = true;
                this._trigger('connect', {});
            }
        };

        this.ws.onclose = () => {
            this.connected = false;
            this.stopPing();
            this._trigger('disconnect', 'closed');
            if (!this.intentionalClose) this.scheduleReconnect();
        };

        this.ws.onerror = (err) => {
            this._trigger('connect_error', err);
        };

        this.ws.onmessage = (msg) => {
            try {
                const parsed = JSON.parse(msg.data);

                if (parsed.action === 'pong') {
                    this.clearPongTimer();
                    return;
                }

                if (parsed.action && parsed.data) {
                    this._trigger(parsed.action, parsed.data);
                } else if (parsed.action) {
                    this._trigger(parsed.action, parsed);
                }
            } catch (e) {
            }
        };
    }

    private scheduleReconnect(): void {
        if (this.intentionalClose || this.reconnectTimer) return;

        const exponential = Math.min(
            RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
            RECONNECT_MAX_MS,
        );
        const jitter = exponential * RECONNECT_JITTER * (Math.random() * 2 - 1);
        const delay = Math.max(RECONNECT_BASE_MS, Math.round(exponential + jitter));

        this.reconnectAttempts += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    private replaySessionState(): void {
        if (this.lastAuthPayload) {
            this.sendRaw({ action: 'authenticate', ...this.lastAuthPayload });
        }
        for (const { roomId } of this.rooms.held()) {
            this.sendRaw({ action: 'subscribe', roomId });
        }
    }

    private startPing(): void {
        this.stopPing();
        this.pingTimer = setInterval(() => {
            if (!this.sendRaw({ action: 'ping' })) return;
            this.clearPongTimer();
            this.pongTimer = setTimeout(() => {
                this.pongTimer = null;
                try { this.ws?.close(); } catch { /* already gone */ }
            }, PONG_TIMEOUT_MS);
        }, PING_INTERVAL_MS);
    }

    private stopPing(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        this.clearPongTimer();
    }

    private clearPongTimer(): void {
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
    }

    private _trigger(event: string, ...args: any[]) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            Array.from(listeners).forEach(cb => {
                try { cb(...args); } catch (err) { console.error(`[Socket] listener for "${event}" threw:`, err); }
            });
        }
    }

    on(event: string, callback: (...args: any[]) => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)!.add(callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        if (!callback) {
            this.eventListeners.delete(event);
        } else {
            this.eventListeners.get(event)?.delete(callback);
        }
    }

    private sendRaw(payload: Record<string, any>): boolean {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
        try {
            this.ws.send(JSON.stringify(payload));
            return true;
        } catch (err) {
            this._trigger('connect_error', err);
            try { this.ws.close(); } catch { /* already gone */ }
            return false;
        }
    }

    emit(event: string, data?: any): void {
        if (event === 'authenticate') {
            this.lastAuthPayload = data ?? {};
        }

        const roomId = roomIdFor(event, data);

        if (UNSUBSCRIBE_EVENTS.has(event)) {
            if (roomId && this.rooms.release(roomId)) {
                this.sendRaw({ action: 'unsubscribe', roomId });
            }
            return;
        }

        if (roomId) {
            // Counted even while the socket is down: replaySessionState
            // subscribes everything held once it opens.
            if (this.rooms.acquire(roomId, event, data)) {
                this.sendRaw({ action: 'subscribe', roomId });
            }
            return;
        }

        this.sendRaw({ action: event, ...data });
    }

    disconnect(): void {
        this.intentionalClose = true;
        this.clearReconnectTimer();
        this.stopPing();
        this.rooms.clear();
        this.lastAuthPayload = null;
        try { this.ws?.close(); } catch { /* already gone */ }
    }
}

export function createSocket(url: string, options?: any): ISocket {
    const resolvedUrl = normalizeSocketUrl(url);

    if (resolvedUrl.startsWith('ws://') || resolvedUrl.startsWith('wss://')) {
        if (resolvedUrl.includes('execute-api')) {
            return new ServerlessWebSocketAdapter(resolvedUrl, options?.getToken);
        }
    }
    return new SocketIOAdapter(resolvedUrl, options);
}
